
import apiClient from './apiClient';
import type {
  SystemStatus,
  ChatStats,
  SelfEvaluationStats,
  ExperimentTimeline,
  Achievement,
  ResearchQuery,
  MemoryLengthData
} from '@/types/api';

// Personas API
export interface Persona {
  name: string;
  description: string;
}

export const getPersonas = async (): Promise<Persona[]> => {
  console.log('Fetching personas from:', apiClient.defaults.baseURL + '/personas');
  try {
    const response = await apiClient.get('/personas');
    console.log('Personas API response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching personas:", error);
    // Log the full error for debugging
    if (error.response) {
      console.error('Error response:', error.response.data);
      console.error('Error status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    // Single fallback persona to clearly show the API isn't connected
    const fallbackPersona = [
      { name: "Lisa_Simpson", description: "FALLBACK PERSONA - API NOT CONNECTED" }
    ];
    console.log('Using fallback persona:', fallbackPersona);
    return fallbackPersona;
  }
};

export const getGoals = async (): Promise<string> => {
  try {
    const response = await apiClient.get('/goals');
    return response.data.goals;
  } catch (error) {
    console.error("Error fetching goals:", error);
    return `Unable to fetch`;
  }
};

export const getModels = async (): Promise<string> => {
  try {
    const response = await apiClient.get('/models');
    return response.data.models;
  } catch (error) {
    console.error("Error fetching models:", error);
    return `Unable to fetch`;
  }
};

export const getReports = async (): Promise<string> => {
  try {
    const response = await apiClient.get('/report');
    return response.data.report;
  } catch (error) {
    console.error("Error fetching reports:", error);
    return `Unable to fetch`;
  }
};

// Research queries API - Used by ResearchQueries.tsx
export const getResearchQueries = async (): Promise<ResearchQuery[]> => {
  try {
    const response = await apiClient.get('/research-queries');
    return response.data;
  } catch (error) {
    console.error("Error fetching research queries:", error);
    // Fallback data
    return [];
  }
};

// System Status API - Used by SystemStatus.tsx
export const getSystemStatus = async (): Promise<SystemStatus[]> => {
  try {
    const response = await apiClient.get('/system-status');
    return response.data;
  } catch (error) {
    console.error("Error fetching system status:", error);
    // Fallback data
    return [
      {
        id: 1,
        name: "Dove Bot System",
        details: "System is operational"
      }
    ];
  }
};

// Self Evaluation Stats API
export const getSelfEvaluationStats = async (): Promise<SelfEvaluationStats> => {
  try {
    const response = await apiClient.get('/self-evaluation-stats');
    return response.data;
  } catch (error) {
    console.error("Error fetching self-evaluation stats:", error);
    // Fallback data
    return {
      percentage: 68,
      completed: 680,
      total: 1000
    };
  }
};

// Experiment Timeline API
export const getExperimentTimeline = async (): Promise<ExperimentTimeline> => {
  try {
    const response = await apiClient.get('/experiment-timeline');
    return response.data;
  } catch (error) {
    console.error("Error fetching experiment timeline:", error);
    // Fallback data
    return {
      daysLeft: 42,
      totalDays: 90,
      startDate: "2023-01-01",
      endDate: "2023-03-31"
    };
  }
};

// Chat Stats API -
export const getChatStats = async (): Promise<ChatStats[]> => {
  try {
    const response = await apiClient.get('/chat-stats');
    return response.data;
  } catch (error) {
    console.error("Error fetching chat stats:", error);
    // Fallback data
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(today.getDate() - i);
      return {
        date: date.toISOString().split('T')[0],
        chats: Math.floor(Math.random() * 50) + 10
      };
    }).reverse();
  }
};

// Memory Length API -
export const getMemoryLengthData = async (): Promise<MemoryLengthData[]> => {
  try {
    const response = await apiClient.get('/memory-length-data');
    return response.data;
  } catch (error) {
    console.error("Error fetching memory length data:", error);
    // Fallback data
    const today = new Date();
    return Array.from({ length: 10 }, (_, i) => {
      const date = new Date();
      date.setDate(today.getDate() - (9 - i)); // Last 10 days
      return {
        date: date.toISOString().split('T')[0],
        length: Math.floor(Math.random() * 9900) + 100 // Random value between 100 and 10000
      };
    });
  }
};

// Mock achievement data
const achievements = [];

export const getAchievements = async () => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));
  return achievements;
};

export interface TodayQuestion {
  id: string;
  persona_name: string;
  question: string;
  date: string;
}

export const getQuestion = async (): Promise<TodayQuestion | null> => {
  try {
    const response = await apiClient.get('/question/today');
    return response.data;
  } catch (error) {
    if ((error as any).response?.status === 404) return null;
    console.error("Error fetching question:", error);
    return null;
  }
};

export interface SubmitData {
  email: string;
  answer: string;
  question_id: string;
}

export const submitAnswer = async (data: SubmitData): Promise<void> => {
  await apiClient.post('/submit', data);
};
